import { makeWorkbook } from "../../utils/excel";
import {
  getIndicatorCandidaturesAdCounter,
  getIndicatorCandidaturesStatesAdCounter,
  getIndicatorCandidaturesGenderAdCounter,
  getIndicatorCandidaturesAgesAdCounter,
  getIndicatorCandidaturesSchoolQualificationAdCounter,
  getIndicatorCandidaturesQualificationLevelsAdCounter,
} from "../../../../../../rest/indicator";
import {
  getDistricts,
  getCountiesByDistrictCode,
} from "../../../../../../rest/administrativeDivision";

const sumBy = (arr, key = "counter") =>
  Array.isArray(arr) ? arr.reduce((s, i) => s + Number(i?.[key] || 0), 0) : 0;

const pctFrac = (v, t) => (t > 0 ? Number(v) / Number(t) : 0);

function genderLabelText(intl, g) {
  const v = String(g ?? "").trim();
  if (!intl) {
    if (v === "0") return "Masculino";
    if (v === "1") return "Feminino";
    return v || "-";
  }
  if (v === "0") {
    return intl.formatMessage({
      id: "indicator.genderForm.M",
      defaultMessage: "Masculino",
    });
  }
  if (v === "1") {
    return intl.formatMessage({
      id: "indicator.genderForm.F",
      defaultMessage: "Feminino",
    });
  }
  return intl.formatMessage({
    id: `promoterForm.gender.${v}`,
    defaultMessage: v || "-",
  });
}

function ageLabelFromToken(token) {
  const v = String(token ?? "")
    .trim()
    .toUpperCase();
  if (v === "65_PLUS") return "> 65";
  if (v.includes("_")) return v.replace("_", "-");
  return v || "-";
}

function aggregateBy(items, keyFn, labelFn) {
  const map = new Map();
  for (const it of items || []) {
    const k = keyFn(it);
    const prev = map.get(k);
    const counter = Number(it?.counter || 0) + Number(prev?.counter || 0);
    const label = prev?.label ?? labelFn(it);
    map.set(k, { key: k, label, counter });
  }
  return Array.from(map.values());
}

function L(intl) {
  return {
    estado: intl
      ? intl.formatMessage({ id: "state.column", defaultMessage: "Estado" })
      : "Estado",
    genero: intl
      ? intl.formatMessage({ id: "gender.column", defaultMessage: "Género" })
      : "Género",
    grupo: intl
      ? intl.formatMessage({
          id: "ageGroup.column",
          defaultMessage: "Grupo Etário",
        })
      : "Grupo Etário",
    escolar: intl
      ? intl.formatMessage({
          id: "schoolQualification.column",
          defaultMessage: "Habilitação Escolar",
        })
      : "Habilitação Escolar",
    nivel: intl
      ? intl.formatMessage({
          id: "qualificationLevel.column",
          defaultMessage: "Nível de Qualificação",
        })
      : "Nível de Qualificação",
    localCounties: intl
      ? intl.formatMessage({
          id: "promoterForm.county",
          defaultMessage: "Concelho",
        })
      : "Concelho",
    localParishes: intl
      ? intl.formatMessage({
          id: "promoterForm.parish",
          defaultMessage: "Freguesia",
        })
      : "Freguesia",
  };
}

function sanitizeFilenamePart(s) {
  return String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\/\\?%*:|"<>]/g, "_")
    .replace(/\s+/g, "_")
    .slice(0, 80);
}

export async function buildWorkbookAdministrativeDivision({
  code,
  level,
  intl,
} = {}) {
  if (!code || !level) {
    throw new Error("buildWorkbookAdministrativeDivision: falta code/level");
  }

  const [pLocal, pStates, pGender, pAge, pSchool, pQual] = await Promise.all([
    getIndicatorCandidaturesAdCounter(code, level).then((r) => r?.data || {}),
    getIndicatorCandidaturesStatesAdCounter(code, level).then(
      (r) => r?.data || {}
    ),
    getIndicatorCandidaturesGenderAdCounter(code, level).then(
      (r) => r?.data || {}
    ),
    getIndicatorCandidaturesAgesAdCounter(code, level).then(
      (r) => r?.data || {}
    ),
    getIndicatorCandidaturesSchoolQualificationAdCounter(code, level).then(
      (r) => r?.data || {}
    ),
    getIndicatorCandidaturesQualificationLevelsAdCounter(code, level).then(
      (r) => r?.data || {}
    ),
  ]);

  const dataLocal = pLocal?.data || [];
  const dataStates = pStates?.data || [];
  const dataGender = pGender?.data || [];
  const dataAge = pAge?.data || [];
  const dataSchool = pSchool?.data || [];
  const dataQualification = pQual?.data || [];

  const labels = L(intl);
  const isCounty = level === "COUNTY";
  const labelLocalText = isCounty ? labels.localParishes : labels.localCounties;

  const statesView =
    level === "DISTRICT"
      ? aggregateBy(
          dataStates,
          (it) => it.state,
          (it) =>
            intl
              ? intl.formatMessage({
                  id: `candidatureState.${it.state}`,
                  defaultMessage: it.state,
                })
              : it.state
        )
      : (dataStates || []).map((it) => ({
          key: it.state,
          label: intl
            ? intl.formatMessage({
                id: `candidatureState.${it.state}`,
                defaultMessage: it.state,
              })
            : it.state,
          counter: Number(it.counter || 0),
        }));

  const genderView =
    level === "DISTRICT"
      ? aggregateBy(
          dataGender,
          (it) => String(it.gender),
          (it) => genderLabelText(intl, it.gender)
        )
      : (dataGender || []).map((it) => ({
          key: String(it.gender),
          label: genderLabelText(intl, it.gender),
          counter: Number(it.counter || 0),
        }));

  const ageView =
    level === "DISTRICT"
      ? aggregateBy(
          dataAge,
          (it) => String(it.secondGroupFilter ?? ""),
          (it) => ageLabelFromToken(it.secondGroupFilter)
        )
      : (dataAge || []).map((it) => ({
          key: String(it.secondGroupFilter ?? ""),
          label: ageLabelFromToken(it.secondGroupFilter),
          counter: Number(it.counter || 0),
        }));

  const schoolView =
    level === "DISTRICT"
      ? aggregateBy(
          dataSchool,
          (it) => String(it.secondGroupFilter ?? ""),
          (it) =>
            intl
              ? intl.formatMessage({
                  id: `schoolQualification.${it.secondGroupFilter}`,
                  defaultMessage: String(it.secondGroupFilter ?? ""),
                })
              : String(it.secondGroupFilter ?? "")
        )
      : (dataSchool || []).map((it) => ({
          key: String(it.secondGroupFilter ?? ""),
          label: intl
            ? intl.formatMessage({
                id: `schoolQualification.${it.secondGroupFilter}`,
                defaultMessage: String(it.secondGroupFilter ?? ""),
              })
            : String(it.secondGroupFilter ?? ""),
          counter: Number(it.counter || 0),
        }));

  const qualView =
    level === "DISTRICT"
      ? aggregateBy(
          dataQualification,
          (it) => String(it.code ?? "-"),
          (it) => String(it.code ?? "-")
        )
      : (dataQualification || []).map((it) => ({
          key: String(it.code ?? "-"),
          label: String(it.code ?? "-"),
          counter: Number(it.counter || 0),
        }));

  const localItems =
    Array.isArray(dataLocal) && dataLocal.length
      ? aggregateBy(
          dataLocal,
          (it) => String(it.description ?? it.code ?? "-"),
          (it) => String(it.description ?? it.code ?? "-")
        ).sort((a, b) => b.counter - a.counter)
      : [];

  const totalEstados = sumBy(statesView);
  const totalGenero = sumBy(genderView);
  const totalAges = sumBy(ageView);
  const totalSchool = sumBy(schoolView);
  const totalQual = sumBy(qualView);
  const totalLocais = sumBy(localItems);

  const resumoAoa = [
    ["Indicador", "Valor"],
    [labels.estado, totalEstados],
    [labels.genero, totalGenero],
    [labels.grupo, totalAges],
    [labels.escolar, totalSchool],
    [labels.nivel, totalQual],
    [labelLocalText, totalLocais],
  ];

  const estadosAoa = [
    [labels.estado, "Total", "% do total"],
    ...statesView
      .slice()
      .sort((a, b) => b.counter - a.counter)
      .map((i) => [
        String(i.label),
        Number(i.counter || 0),
        pctFrac(i.counter || 0, totalEstados),
      ]),
  ];

  const generoAoa = [
    [labels.genero, "Total", "% do total"],
    ...genderView
      .slice()
      .sort((a, b) => b.counter - a.counter)
      .map((i) => [
        String(i.label),
        Number(i.counter || 0),
        pctFrac(i.counter || 0, totalGenero),
      ]),
  ];

  const idadesAoa = [
    [labels.grupo, "Total", "% do total"],
    ...ageView
      .slice()
      .sort((a, b) => b.counter - a.counter)
      .map((i) => [
        String(i.label),
        Number(i.counter || 0),
        pctFrac(i.counter || 0, totalAges),
      ]),
  ];

  const schoolAoa = [
    [labels.escolar, "Total", "% do total"],
    ...schoolView
      .slice()
      .sort((a, b) => b.counter - a.counter)
      .map((i) => [
        String(i.label),
        Number(i.counter || 0),
        pctFrac(i.counter || 0, totalSchool),
      ]),
  ];

  const qualAoa = [
    [labels.nivel, "Total", "% do total"],
    ...qualView
      .slice()
      .sort((a, b) => b.counter - a.counter)
      .map((i) => [
        String(i.label),
        Number(i.counter || 0),
        pctFrac(i.counter || 0, totalQual),
      ]),
  ];

  const localAoa = [
    [labelLocalText, "Total", "% do total"],
    ...localItems.map((i) => [
      String(i.key || i.label || "-"),
      Number(i.counter || 0),
      pctFrac(i.counter || 0, totalLocais),
    ]),
  ];

  return makeWorkbook([
    { name: "Resumo", aoa: resumoAoa },
    { name: "Estado", aoa: estadosAoa, percentCols: [2] },
    { name: "Genero", aoa: generoAoa, percentCols: [2] },
    { name: "Grupo_Etario", aoa: idadesAoa, percentCols: [2] },
    { name: "Habilitacao_Escolar", aoa: schoolAoa, percentCols: [2] },
    { name: "Nivel_Qualificacao", aoa: qualAoa, percentCols: [2] },
    {
      name: isCounty ? "Freguesia" : "Concelho",
      aoa: localAoa,
      percentCols: [2],
    },
  ]);
}

export async function buildAdministrativeDivisionAll({ intl } = {}) {
  const { data: districts = [] } = await getDistricts();
  const safeDistricts = (districts || []).map((d) => ({
    code: d?.code,
    description: d?.description || d?.name || String(d?.code || ""),
  }));

  const results = [];

  for (const d of safeDistricts) {
    if (!d?.code) continue;

    const wb = await buildWorkbookAdministrativeDivision({
      code: d.code,
      level: "DISTRICT",
      intl,
    });
    const filename = `indicadores_divisao_administrativa_distrito_${sanitizeFilenamePart(
      d.code
    )}_${sanitizeFilenamePart(d.description)}.xlsx`;
    results.push({ folder: "distritos", filename, workbook: wb });

    const { data: counties = [] } = await getCountiesByDistrictCode(d.code);
    for (const c of counties || []) {
      if (!c?.code) continue;
      const wbCounty = await buildWorkbookAdministrativeDivision({
        code: c.code,
        level: "COUNTY",
        intl,
      });
      const filenameCounty = `indicadores_divisao_administrativa_concelho_${sanitizeFilenamePart(
        c.code
      )}_${sanitizeFilenamePart(c.description || c.name || "")}.xlsx`;
      results.push({
        folder: "concelhos",
        filename: filenameCounty,
        workbook: wbCounty,
      });
    }
  }

  return results;
}
